import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Users2, CheckCircle2, XCircle, MessageCircleQuestion, Loader2, Trash2, FileText, Star, Search,
} from "lucide-react";
import api from "@/lib/api";
import { AdminModal } from "@/components/AdminModal";
import { resolveTrainerImage } from "@/lib/trainers";
import { Header } from "./Schools";

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-gray-100 text-gray-700" },
  changes_requested: { label: "Changes requested", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-rose-100 text-rose-700" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>{m.label}</span>;
}

export default function AdminExpertApplications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [changesNote, setChangesNote] = useState("");
  const [showChanges, setShowChanges] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = statusFilter === "all" ? {} : { status: statusFilter };
    api.get("/experts/applications", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => { toast.error("Could not load applications"); setItems([]); })
      .finally(() => setLoading(false));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((a) =>
    !q || [a.full_name, a.school, a.subject_expertise].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))
  );

  const updateStatus = async (id, status, extra = {}) => {
    setBusy(true);
    try {
      await api.patch(`/experts/applications/${id}`, { status, ...extra });
      toast.success(`Marked as ${STATUS_META[status]?.label || status}`);
      load();
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch { toast.error("Could not update status"); }
    finally { setBusy(false); }
  };

  const toggleFeatured = async (a) => {
    setBusy(true);
    try {
      await api.patch(`/experts/applications/${a.id}`, { featured: !a.featured });
      load();
      if (selected?.id === a.id) setSelected((s) => ({ ...s, featured: !s.featured }));
    } catch { toast.error("Could not update"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/experts/applications/${id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Header title="Skilled Teacher Applications" subtitle={`${filtered.length} application${filtered.length === 1 ? "" : "s"}`} />

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {["all", ...Object.keys(STATUS_META)].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${statusFilter === s ? "bg-[#0F172A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >
            {s === "all" ? "All" : STATUS_META[s].label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, school, subject…"
            className="pl-8 pr-3 py-1.5 rounded-full border border-gray-200 text-xs outline-none w-56"
            data-testid="expert-apps-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="mt-6 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
          <Users2 className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <div className="font-display text-lg font-medium text-[#0F172A]">No Applications Yet</div>
          <p className="mt-1 text-sm text-gray-500">Skilled Teacher applications will appear here.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {filtered.map((a) => (
            <div key={a.id} className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`expert-app-row-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  {a.photo ? (
                    <img src={resolveTrainerImage(a.photo)} alt={a.full_name} className="w-10 h-10 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-100 grid place-items-center shrink-0 text-gray-400 text-sm font-semibold">
                      {(a.full_name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-[#0F172A]">{a.full_name}</h3>
                      <StatusBadge status={a.status} />
                      {a.featured && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-3">
                      <span>{a.designation} · {a.school}</span>
                      <span>{a.subject_expertise}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => setSelected(a)} className="text-xs font-medium bg-[#EEF2FF] text-[#4F46E5] px-3 py-1.5 rounded-lg hover:bg-indigo-100">
                    Review
                  </button>
                  <button onClick={() => remove(a.id)} disabled={busy} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-50 text-gray-500 hover:text-rose-600 disabled:opacity-40">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <AdminModal
          onClose={() => setSelected(null)}
          testId="expert-app-detail"
          maxWidth="max-w-2xl"
          title={selected.full_name}
          subtitle={`${selected.designation} · ${selected.school} · ${selected.city || ""} ${selected.state || ""}`}
          footer={
            <div className="flex flex-wrap gap-2 w-full">
              {selected.status !== "approved" && (
                <button
                  onClick={() => updateStatus(selected.id, "approved")}
                  disabled={busy}
                  data-testid="expert-app-approve"
                  className="text-xs font-medium bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Approve &amp; Publish
                </button>
              )}
              {selected.status !== "rejected" && (
                <button onClick={() => setShowChanges(true)} className="text-xs font-medium bg-amber-100 text-amber-700 px-3 py-2 rounded-lg hover:bg-amber-200 inline-flex items-center gap-1">
                  <MessageCircleQuestion className="w-3.5 h-3.5" /> Request Changes
                </button>
              )}
              {selected.status !== "rejected" && (
                <button onClick={() => setShowReject(true)} className="text-xs font-medium bg-rose-100 text-rose-700 px-3 py-2 rounded-lg hover:bg-rose-200 inline-flex items-center gap-1">
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </button>
              )}
            </div>
          }
        >
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Info label="Email" value={selected.email} />
              <Info label="Phone" value={selected.phone} />
              <Info label="Subject expertise" value={selected.subject_expertise} />
              <Info label="Experience" value={selected.experience || "—"} />
              <Info label="Skills" value={(selected.skills || []).join(", ") || "—"} />
              <Info label="Languages" value={(selected.languages || []).join(", ") || "—"} />
              <Info label="Preferred training location" value={selected.preferred_location || "—"} />
              <Info label="LinkedIn / Website" value={selected.linkedin || selected.website || "—"} />
            </div>
            {selected.bio && <Info label="Biography" value={selected.bio} full />}
            {selected.achievements?.length > 0 && <Info label="Achievements" value={selected.achievements.join(", ")} full />}

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              {selected.resume_url && (
                <a href={resolveTrainerImage(selected.resume_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">
                  <FileText className="w-3 h-3" /> Resume
                </a>
              )}
              {(selected.certificate_urls || []).map((u, i) => (
                <a key={i} href={resolveTrainerImage(u)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">
                  <FileText className="w-3 h-3" /> Certificate {i + 1}
                </a>
              ))}
              <button
                onClick={() => toggleFeatured(selected)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${selected.featured ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}
              >
                <Star className="w-3 h-3" /> {selected.featured ? "Featured" : "Mark featured"}
              </button>
            </div>

            {selected.admin_notes && (
              <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 p-3 text-xs text-amber-800">
                <span className="font-medium">Notes on file: </span>{selected.admin_notes}
              </div>
            )}
        </AdminModal>
      )}

      {showReject && (
        <AdminModal
          onClose={() => setShowReject(false)}
          title="Reject application"
          testId="expert-reject-modal"
          footer={
            <>
              <button onClick={() => setShowReject(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg w-full sm:w-auto">Cancel</button>
              <button
                onClick={async () => { await updateStatus(selected.id, "rejected", { admin_notes: rejectReason }); setShowReject(false); setRejectReason(""); }}
                className="px-4 py-2 text-sm font-medium bg-rose-600 text-white rounded-lg hover:bg-rose-700 w-full sm:w-auto"
              >
                Reject
              </button>
            </>
          }
        >
          <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (shared with the applicant)" rows={3} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm resize-none" />
        </AdminModal>
      )}

      {showChanges && (
        <AdminModal
          onClose={() => setShowChanges(false)}
          title="Request changes"
          testId="expert-changes-modal"
          footer={
            <>
              <button onClick={() => setShowChanges(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg w-full sm:w-auto">Cancel</button>
              <button
                onClick={async () => { await updateStatus(selected.id, "changes_requested", { admin_notes: changesNote }); setShowChanges(false); setChangesNote(""); }}
                className="px-4 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 w-full sm:w-auto"
              >
                Send request
              </button>
            </>
          }
        >
          <textarea value={changesNote} onChange={(e) => setChangesNote(e.target.value)} placeholder="What should the applicant update?" rows={3} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm resize-none" />
        </AdminModal>
      )}
    </motion.div>
  );
}

const Info = ({ label, value, full }) => (
  <div className={full ? "sm:col-span-2 mt-3" : ""}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-medium text-[#0F172A]">{value}</div>
  </div>
);
